import React, { useEffect, useRef, useState, useCallback } from 'react';
import Markdown from 'markdown-to-jsx';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Collapse,
  Chip,
  Badge,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ChatAction } from '../../models';
import {
  closeChat,
  setModel,
  sendMessage,
  pollConversation,
  fetchConversations,
  loadConversation,
  newChat,
  fetchKnowledgeNotes,
  updateKnowledgeNote,
  fetchChatActions,
  confirmChatAction,
  cancelChatAction,
  selectChatOpen,
  selectChatMessages,
  selectChatModel,
  selectIsRunning,
  selectPollNonce,
  selectConversations,
  selectChatCost,
  selectPendingNotes,
  selectActiveNotes,
  selectFetchKnowledgeLoading,
  selectChatActions,
} from './chatSlice';

import './ChatPanel.scss';

const MODELS = [
  { value: 'claude-opus-4-8', label: 'Opus 4.8' },
  { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
];

export function modelLabel(modelId: string): string {
  return MODELS.find(m => m.value === modelId)?.label ?? modelId;
}

const POLL_INTERVAL_MS = 1000;
const POLL_MAX_ELAPSED_MS = 90_000;

function formatRelativeDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/** Convert a snake_case tool name to a human-readable label. */
export function toolLabel(name: string): string {
  // Known overrides for multi-word names that don't title-case nicely
  const overrides: Record<string, string> = {
    driver_safety: 'Driver safety',
    dump_events: 'Dump events',
    dump_yards: 'Dump yards',
    schedule_items: 'Schedule items',
    fleet_optimizations: 'Fleet optimizations',
    punch_cards: 'Punch cards',
    pay_periods: 'Pay periods',
    dump_yard_templates: 'Dump yard templates',
    permanent_rentals: 'Permanent rentals',
    early_pickup_requests: 'Early pickup requests',
    dvir_reports: 'DVIR reports',
    dvir_templates: 'DVIR templates',
    dvir_questions: 'DVIR questions',
  };
  if (overrides[name]) return overrides[name];
  // Fallback: replace underscores with spaces and title-case each word
  return name
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface ActionConfirmCardProps {
  action: ChatAction;
  onConfirm: (id: string) => void;
  onCancel: (id: string) => void;
}

const ActionConfirmCard: React.FC<ActionConfirmCardProps> = ({ action, onConfirm, onCancel }) => {
  const isPending = action.status === 'pending';
  const isExecuted = action.status === 'executed';
  const isFailed = action.status === 'failed';
  const isCanceled = action.status === 'canceled';
  const isResolved = isExecuted || isFailed || isCanceled;

  return (
    <Paper
      className={`chat-panel__action-card${isResolved ? ' chat-panel__action-card--resolved' : ''}`}
      elevation={0}
      aria-label={`Action proposal: ${action.preview}`}
    >
      <Box className="chat-panel__action-card-header">
        <Typography variant="caption" className="chat-panel__action-card-label">
          Proposed action
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {action.tool} / {action.op}
        </Typography>
      </Box>
      <Typography variant="body2" className="chat-panel__action-card-preview" sx={{ mb: 1 }}>
        {action.preview}
      </Typography>
      {isPending && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => onConfirm(action.id)}
            aria-label={`Confirm action: ${action.preview}`}
          >
            Confirm
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => onCancel(action.id)}
            aria-label={`Cancel action: ${action.preview}`}
          >
            Cancel
          </Button>
        </Box>
      )}
      {isExecuted && (
        <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
          ✓ Applied
        </Typography>
      )}
      {isFailed && (
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
          ✗ Failed{action.result != null ? `: ${JSON.stringify(action.result)}` : ''}
        </Typography>
      )}
      {isCanceled && (
        <Typography variant="body2" color="text.secondary">
          Canceled
        </Typography>
      )}
    </Paper>
  );
};

export const ChatPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectChatOpen);
  const messages = useAppSelector(selectChatMessages);
  const model = useAppSelector(selectChatModel);
  const isRunning = useAppSelector(selectIsRunning);
  const pollNonce = useAppSelector(selectPollNonce);
  const conversations = useAppSelector(selectConversations) ?? [];
  const costUsd = useAppSelector(selectChatCost);
  const conversationId = useAppSelector(
    (state: any) => state.chat.conversationId as string | undefined
  );
  const pendingNotes = useAppSelector(selectPendingNotes);
  const activeNotes = useAppSelector(selectActiveNotes);
  const knowledgeLoading = useAppSelector(selectFetchKnowledgeLoading);
  const chatActions = useAppSelector(selectChatActions);

  const [inputValue, setInputValue] = useState('');
  const [chatOff, setChatOff] = useState(false);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [activeNotesOpen, setActiveNotesOpen] = useState(false);
  /** id → draft text for inline edit */
  const [editDrafts, setEditDrafts] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<boolean>(
    () => localStorage.getItem('chatPanelExpanded') === 'true'
  );

  const toggleExpanded = () => {
    setExpanded(prev => {
      const next = !prev;
      localStorage.setItem('chatPanelExpanded', String(next));
      return next;
    });
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastConvRef = useRef<string | undefined>(undefined);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number | null>(null);

  // Auto-scroll to the latest message — but only when the user is already near
  // the bottom, so streaming/poll updates don't yank them down while they've
  // scrolled up to read a previous answer. Always jump to bottom on a
  // conversation switch (so you land on the latest message).
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const convChanged = lastConvRef.current !== conversationId;
    lastConvRef.current = conversationId;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (convChanged || nearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: convChanged ? 'auto' : 'smooth' });
    }
  }, [messages, conversationId]);

  // Fetch conversation list on open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchConversations());
    }
  }, [isOpen, dispatch]);

  // Fetch knowledge notes on open so the badge count is visible immediately
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchKnowledgeNotes());
    }
  }, [isOpen, dispatch]);

  // Fetch actions whenever conversationId is set (on load/start) and after each
  // completed turn (isRunning flips false with an active conversation).
  const prevIsRunningRef = useRef<boolean>(false);
  useEffect(() => {
    const justFinished = prevIsRunningRef.current && !isRunning;
    prevIsRunningRef.current = isRunning;
    if (conversationId && (justFinished || (!isRunning && conversationId))) {
      dispatch(fetchChatActions({ conversationId }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isRunning, dispatch]);

  // Polling loop: runs while isOpen && isRunning and there is a conversationId.
  // pollNonce in deps ensures re-arm on every new send (I1).
  // isOpen in deps ensures the interval stops when the drawer is closed (I2).
  useEffect(() => {
    const clearPoll = () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      pollStartRef.current = null;
    };

    if (isOpen && isRunning && conversationId) {
      // Always start a fresh interval when the effect re-runs (new nonce = new send).
      clearPoll();
      pollStartRef.current = Date.now();
      pollIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - (pollStartRef.current ?? 0);
        if (elapsed >= POLL_MAX_ELAPSED_MS) {
          clearPoll();
          setPollingTimedOut(true);
          return;
        }
        dispatch(pollConversation({ conversationId: conversationId! }));
      }, POLL_INTERVAL_MS);
    } else {
      clearPoll();
    }

    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isRunning, conversationId, pollNonce]);

  const sendDisabled = !inputValue.trim() || isRunning;

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isRunning) return;
    setInputValue('');
    setChatOff(false);
    setPollingTimedOut(false);
    try {
      await dispatch(sendMessage({ message: text })).unwrap();
      // Refresh conversation list after send so new conversations appear
      dispatch(fetchConversations());
    } catch (err: any) {
      // err is the rejectWithValue payload: { status, message }
      // Fall back to checking message string for robustness
      const status = err?.status ?? err?.response?.status;
      const msg: string = err?.message ?? '';
      if (status === 503 || msg.toLowerCase().includes('503') || msg.toLowerCase().includes('service unavailable')) {
        setChatOff(true);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !sendDisabled) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    dispatch(newChat());
    setHistoryOpen(false);
    setChatOff(false);
    setPollingTimedOut(false);
  };

  const handleSelectConversation = (id: string) => {
    dispatch(loadConversation({ conversationId: id }));
    setHistoryOpen(false);
    setChatOff(false);
    setPollingTimedOut(false);
  };

  // Knowledge helpers
  const handleApproveNote = useCallback((id: string) => {
    dispatch(updateKnowledgeNote({ id, status: 'active' }));
  }, [dispatch]);

  const handleRejectNote = useCallback((id: string) => {
    dispatch(updateKnowledgeNote({ id, status: 'rejected' }));
  }, [dispatch]);

  const handleStartEdit = useCallback((id: string, currentContent: string) => {
    setEditDrafts(prev => ({ ...prev, [id]: currentContent }));
  }, []);

  const handleCancelEdit = useCallback((id: string) => {
    setEditDrafts(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const handleSaveEdit = useCallback((id: string) => {
    const content = editDrafts[id];
    if (content == null) return;
    dispatch(updateKnowledgeNote({ id, content })).then(() => {
      setEditDrafts(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    });
  }, [dispatch, editDrafts]);

  const handleRemoveActive = useCallback((id: string) => {
    dispatch(updateKnowledgeNote({ id, status: 'rejected' }));
  }, [dispatch]);

  // Action confirm/cancel handlers
  const handleConfirmAction = useCallback((id: string) => {
    dispatch(confirmChatAction({ id }));
  }, [dispatch]);

  const handleCancelAction = useCallback((id: string) => {
    dispatch(cancelChatAction({ id }));
  }, [dispatch]);

  const handleToggleKnowledge = () => {
    setKnowledgeOpen(v => !v);
    if (!knowledgeOpen) {
      setHistoryOpen(false);
    }
  };

  const handleToggleHistory = () => {
    setHistoryOpen(v => !v);
    if (!historyOpen) {
      setKnowledgeOpen(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => dispatch(closeChat())}
      PaperProps={{
        className: `chat-panel__paper${expanded ? ' chat-panel__paper--expanded' : ''}`,
      }}
    >
      <Box className="chat-panel">
        <Box className="chat-panel__header">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">AI Assistant</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={toggleExpanded}
                aria-label={expanded ? 'Collapse chat' : 'Expand chat'}
              >
                {expanded ? 'Collapse' : 'Expand'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleToggleHistory}
                aria-label="Toggle history"
              >
                History
              </Button>
              <Badge
                badgeContent={pendingNotes.length}
                color="warning"
                invisible={pendingNotes.length === 0}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleToggleKnowledge}
                  aria-label={`Toggle knowledge panel${pendingNotes.length > 0 ? `, ${pendingNotes.length} pending` : ''}`}
                >
                  Knowledge
                </Button>
              </Badge>
              <Button
                size="small"
                variant="outlined"
                onClick={handleNewChat}
                aria-label="New chat"
              >
                New chat
              </Button>
            </Box>
          </Box>
        </Box>

        {/* History list */}
        <Collapse in={historyOpen}>
          <Box className="chat-panel__history">
            {conversations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                No previous conversations.
              </Typography>
            ) : (
              <List dense disablePadding>
                {conversations.map(conv => (
                  <ListItemButton
                    key={conv.id}
                    selected={conv.id === conversationId}
                    onClick={() => handleSelectConversation(conv.id)}
                    sx={{ px: 2, py: 0.5 }}
                  >
                    <ListItemText
                      primary={conv.title || 'Untitled chat'}
                      secondary={formatRelativeDate(conv.updatedAt)}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
          <Divider />
        </Collapse>

        {/* Knowledge panel */}
        <Collapse in={knowledgeOpen}>
          <Box className="chat-panel__knowledge">
            {knowledgeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} aria-label="Loading knowledge notes" />
              </Box>
            ) : (
              <>
                {/* Pending proposals */}
                <Box className="chat-panel__knowledge-section">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="chat-panel__knowledge-section-title"
                  >
                    Pending review ({pendingNotes.length})
                  </Typography>
                  {pendingNotes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                      No proposals to review.
                    </Typography>
                  ) : (
                    pendingNotes.map(note => (
                      <Box key={note.id} className="chat-panel__knowledge-note">
                        {editDrafts[note.id] != null ? (
                          <>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              maxRows={6}
                              value={editDrafts[note.id]}
                              onChange={e =>
                                setEditDrafts(prev => ({ ...prev, [note.id]: e.target.value }))
                              }
                              aria-label="Edit note content"
                            />
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSaveEdit(note.id)}
                                aria-label="Save note edit"
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleCancelEdit(note.id)}
                                aria-label="Cancel note edit"
                              >
                                Cancel
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ mb: 0.25 }}>
                              {note.content}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              from a conversation · {formatRelativeDate(note.createdAt)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleApproveNote(note.id)}
                                aria-label="Approve note"
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleStartEdit(note.id, note.content)}
                                aria-label="Edit note"
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleRejectNote(note.id)}
                                aria-label="Reject note"
                              >
                                Reject
                              </Button>
                            </Box>
                          </>
                        )}
                      </Box>
                    ))
                  )}
                </Box>

                {/* Active notes (collapsible sub-section) */}
                <Box className="chat-panel__knowledge-section">
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', px: 2, py: 0.5 }}
                    onClick={() => setActiveNotesOpen(v => !v)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && setActiveNotesOpen(v => !v)}
                    aria-expanded={activeNotesOpen}
                    aria-label={`Toggle active knowledge section, ${activeNotes.length} notes`}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Active knowledge ({activeNotes.length})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activeNotesOpen ? '▲' : '▼'}
                    </Typography>
                  </Box>
                  <Collapse in={activeNotesOpen}>
                    {activeNotes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                        No active notes yet.
                      </Typography>
                    ) : (
                      activeNotes.map(note => (
                        <Box key={note.id} className="chat-panel__knowledge-note">
                          {editDrafts[note.id] != null ? (
                            <>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                maxRows={6}
                                value={editDrafts[note.id]}
                                onChange={e =>
                                  setEditDrafts(prev => ({ ...prev, [note.id]: e.target.value }))
                                }
                                aria-label="Edit active note content"
                              />
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleSaveEdit(note.id)}
                                  aria-label="Save active note edit"
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleCancelEdit(note.id)}
                                  aria-label="Cancel active note edit"
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2" sx={{ mb: 0.25 }}>
                                {note.content}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleStartEdit(note.id, note.content)}
                                  aria-label="Edit active note"
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRemoveActive(note.id)}
                                  aria-label="Remove active note"
                                >
                                  Remove
                                </Button>
                              </Box>
                            </>
                          )}
                        </Box>
                      ))
                    )}
                  </Collapse>
                </Box>
              </>
            )}
          </Box>
          <Divider />
        </Collapse>

        {/* Model picker */}
        <Box className="chat-panel__model-picker">
          <FormControl size="small" fullWidth>
            <InputLabel id="chat-model-label">Model</InputLabel>
            <Select
              labelId="chat-model-label"
              label="Model"
              value={model}
              onChange={e => dispatch(setModel(e.target.value as string))}
            >
              {MODELS.map(m => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Messages */}
        <Box className="chat-panel__messages" ref={messagesContainerRef}>
          {messages.map((msg, idx) => (
            <Paper
              key={msg.id ?? idx}
              className={`chat-panel__message chat-panel__message--${msg.role}`}
              elevation={0}
            >
              {msg.status === 'running' && msg.content === '' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="body2" color="text.secondary">Thinking…</Typography>
                </Box>
              ) : (
                <>
                  {msg.role === 'assistant' ? (
                    <Box className="chat-panel__markdown">
                      <Markdown options={{ disableParsingRawHTML: true }}>
                        {msg.content}
                      </Markdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  )}
                  {msg.role === 'assistant' && msg.toolsUsed && msg.toolsUsed.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }} aria-label="Tools used">
                      {msg.toolsUsed.map(tool => (
                        <Chip
                          key={tool}
                          label={toolLabel(tool)}
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Paper>
          ))}
          {/* Action confirm cards */}
          {chatActions.length > 0 && (
            <Box className="chat-panel__actions-area" aria-label="Pending action proposals">
              {chatActions.map((action: ChatAction) => (
                <ActionConfirmCard
                  key={action.id}
                  action={action}
                  onConfirm={handleConfirmAction}
                  onCancel={handleCancelAction}
                />
              ))}
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Status alerts */}
        {chatOff && (
          <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
            Chat is currently off. Please try again later.
          </Alert>
        )}
        {pollingTimedOut && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            This is taking too long — please try again.
          </Alert>
        )}

        {/* Cost display */}
        <Box sx={{ px: 2, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Cost: ${costUsd != null ? (parseFloat(costUsd) || 0).toFixed(4) : '0.0000'}
          </Typography>
        </Box>

        {/* Input */}
        <Box className="chat-panel__input-row">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Ask about orders, schedules…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            aria-label="Send"
            variant="contained"
            disabled={sendDisabled}
            onClick={handleSend}
            sx={{ ml: 1, minWidth: 0 }}
          >
            Send
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
